import { useEffect } from 'react'

import {
  useViewingRoomStore,
  useSelectedMarkerStore,
  SelectedMarker,
  useSidebarStore,
  type TSubwayMarker,
} from '@/entities/map'
import { eventManager, mapManager, useOverlay } from '@/shared/lib'

import {
  type SubwayOverlay,
  type SubwayOverlayProps,
  createSubwayOverlay,
} from '.'

import { getContent } from './element'

export const useSubwayOverlay = () => {
  const sidebarActions = useSidebarStore(state => state.actions)

  const isSelected = useSelectedMarkerStore(state => state.isSelected)
  const selectMarker = useSelectedMarkerStore(
    state => state.actions.selectMarker,
  )

  const resetViewingRoom = useViewingRoomStore(
    state => state.actions.resetViewingRoom,
  )

  const updateSelectedMarker = (marker?: SelectedMarker) => {
    mapManager.withMap(map => {
      const zoomLevel = map.getZoom()

      if (marker) {
        resetViewingRoom()
      }

      if (marker && marker.nextZoomLevel && marker.nextZoomLevel <= zoomLevel) {
        selectMarker(marker)
      }
    })
  }

  const updateSidebar = () => {
    const isSidebarOpen = useSidebarStore.getState().isOpen

    if (!isSidebarOpen) {
      sidebarActions.open()

      setTimeout(() => {
        eventManager.triggerEvent('resize')
      }, 0)
    }
  }

  const handleSubwayOverlayClick = (marker?: SelectedMarker) => {
    updateSelectedMarker(marker)
    updateSidebar()
  }

  const subwayOverlay = useOverlay<
    SubwayOverlay,
    SubwayOverlayProps,
    TSubwayMarker
  >({
    createInstance: createSubwayOverlay,
    getOverlayOptions: item => ({
      zIndex: 20,
      content: getContent(item.name),
      nextZoomLevel: item.nextZoomLevel,
      onClick: handleSubwayOverlayClick,
    }),
  })

  const handleSelectedMarkerChange = () => {
    if (!subwayOverlay.ref.current) return

    subwayOverlay.ref.current.forEach(overlay => {
      if (overlay.isSelected && !isSelected(overlay.id)) {
        overlay.markAsDeselected()
      }
    })
  }

  useEffect(
    () =>
      useSelectedMarkerStore.subscribe(
        state => state.selectedMarker,
        handleSelectedMarkerChange,
      ),
    [],
  )

  return subwayOverlay
}
